import { API } from "@swivl-mono/api-helper";
import { filterClasses, normalizeCallScriptSteps, resolveStepLibrary } from "@swivl-mono/helpers";
import type { ResolvedLibraryStep } from "@swivl-mono/helpers";
import type { CallScriptHistoryEntry, CallScriptHistoryScriptEntry, CallScriptHistoryStepEntry } from "@swivl-mono/types";
import { findCallScriptLibraryStep } from "@swivl-mono/types";
import { Button, ButtonSize, ButtonVariant } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";

import LocationsModel from "../../../Models/Locations/Locations.Model";
import MembersModel from "../../../Models/Member/Members.Model";

/**
 * History tab (Chuck, 2026-10-01): every change to the org's call scripts and steps, newest first,
 * including scripts reverted to inherited and steps deleted — the per-script History inside the
 * edit flyout cannot show those because the script is gone. Read-only; restores stay in the flyout.
 * Lossless: nothing is capped or truncated, the list pages with "Show earlier changes".
 */

interface CallScriptHistorySectionProps {
  orgId: number;
  onError: (message: string | null) => void;
}

function memberName(memberId: number | null): string {
  if (memberId == null) return "Swivl";
  const member = MembersModel.state.members?.find((m) => m.id === memberId);
  return member?.agentName || member?.account?.email || `Member #${memberId}`;
}

function scopeLabel(entry: CallScriptHistoryScriptEntry): string {
  if (entry.locationId != null) {
    const location = LocationsModel.state.locations?.find((l) => l.id === entry.locationId);
    return location ? `Location · ${location.name}` : `Location #${entry.locationId}`;
  }
  if (entry.groupId != null) {
    const group = LocationsModel.state.groups?.find((g) => g.id === entry.groupId);
    return group ? `Group · ${group.name}` : `Group #${entry.groupId}`;
  }
  return "Organization";
}

/** The version note, as a sentence an operator reads. The note itself is kept on the entry. */
function actionLabel(entry: CallScriptHistoryEntry): string {
  const note = entry.note;
  if (note === "saved") return entry.revision === 1 ? "Created" : "Saved";
  if (note === "removed here") return "Removed at this scope (saved with no steps)";
  if (note === "reverted to inherited") return "Reverted to the inherited version";
  if (note === "reverted to default") return "Reverted to the platform default";
  if (note === "deleted") return "Deleted";
  if (note.startsWith("restored from revision")) return note.charAt(0).toUpperCase() + note.slice(1);
  return note;
}

function subjectLabel(entry: CallScriptHistoryEntry): string {
  return entry.kind === "script" ? `Script · ${entry.snapshot.name}` : `Step · ${entry.snapshot.label}`;
}

function entryKey(entry: CallScriptHistoryEntry): string {
  return `${entry.kind}-${entry.id}`;
}

function ScriptSnapshot({
  entry,
  library
}: {
  entry: CallScriptHistoryScriptEntry;
  library: ReadonlyArray<ResolvedLibraryStep>;
}): React.ReactElement {
  const steps = normalizeCallScriptSteps(entry.snapshot.steps);
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
      <dt className="text-grey-500">Use this when</dt>
      <dd className="text-grey-800">{entry.snapshot.whenToUse || "—"}</dd>
      <dt className="text-grey-500">Steps</dt>
      <dd className="text-grey-800">
        {steps.length === 0 ? (
          "(no steps)"
        ) : (
          <ol className="list-decimal space-y-0.5 pl-5">
            {steps.map((step, i) => (
              <li key={`${step.slug}-${i}`}>
                {findCallScriptLibraryStep(step.slug, library)?.label ?? step.slug}
                {step.requirements.length > 0 && <span className="text-grey-500"> · must have: {step.requirements.join(", ")}</span>}
              </li>
            ))}
          </ol>
        )}
      </dd>
      <dt className="text-grey-500">For sequences only</dt>
      <dd className="text-grey-800">{entry.snapshot.forSequencesOnly ? "Yes" : "No"}</dd>
    </dl>
  );
}

function StepSnapshot({ entry }: { entry: CallScriptHistoryStepEntry }): React.ReactElement {
  const s = entry.snapshot;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
      <dt className="text-grey-500">Group</dt>
      <dd className="text-grey-800">{s.group}</dd>
      <dt className="text-grey-500">Sentence</dt>
      <dd className="text-grey-800">{s.sentence}</dd>
      <dt className="text-grey-500">Agent copy</dt>
      <dd className="text-grey-800">{s.liveAgentCopy || "—"}</dd>
      <dt className="text-grey-500">Completion criteria</dt>
      <dd className="text-grey-800">{s.coachingCompletionCriteria || "—"}</dd>
      <dt className="text-grey-500">Shown to agents</dt>
      <dd className="text-grey-800">{s.showToAgents ? "Yes" : "No"}</dd>
      <dt className="text-grey-500">Must-haves</dt>
      <dd className="text-grey-800">
        {s.requirementsAllowed ? (s.defaultRequirements.length > 0 ? s.defaultRequirements.join(", ") : "allowed, none by default") : "not allowed"}
      </dd>
    </dl>
  );
}

export function CallScriptHistorySection({ orgId, onError }: CallScriptHistorySectionProps): React.ReactElement {
  const [entries, setEntries] = useState<CallScriptHistoryEntry[] | null>(null);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [library, setLibrary] = useState<ResolvedLibraryStep[]>(() => resolveStepLibrary([]));
  const [open, setOpen] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    Promise.all([API.CallScript.listHistory(orgId), API.CallScriptStep.listRows(orgId).catch(() => [])])
      .then(([page, stepRows]) => {
        if (cancelled) return;
        setEntries(page.entries);
        setNextBefore(page.nextBefore);
        setLibrary(resolveStepLibrary(stepRows));
      })
      .catch((e: unknown) => {
        if (!cancelled) onError(e instanceof Error ? e.message : "Could not load the history.");
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, onError]);

  const handleLoadMore = async (): Promise<void> => {
    if (!nextBefore) return;
    setLoadingMore(true);
    try {
      const page = await API.CallScript.listHistory(orgId, nextBefore);
      setEntries((prev) => [...(prev ?? []), ...page.entries]);
      setNextBefore(page.nextBefore);
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : "Could not load earlier changes.");
    } finally {
      setLoadingMore(false);
    }
  };

  const handleToggle = (key: string): void => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <section className="space-y-3">
      <p className="text-sm text-grey-600">
        Every change to this organization&apos;s call scripts and steps, newest first — including scripts reverted to the inherited version and steps
        that were deleted. Open a change to see what was saved. To restore an older version of a script that still exists, use History in that
        script&apos;s editor.
      </p>
      {!entries ? (
        <p className="text-sm text-grey-500">Loading history…</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-grey-500">No changes recorded yet.</p>
      ) : (
        <ul className="space-y-2">
          {entries.map((entry) => {
            const key = entryKey(entry);
            const expanded = open.has(key);
            return (
              <li key={key} className="rounded border border-grey-200 bg-white">
                <button type="button" className="flex w-full items-start justify-between gap-3 p-3 text-left" onClick={() => handleToggle(key)}>
                  <div className="min-w-0 text-sm">
                    <p className="font-semibold text-grey-900">
                      {subjectLabel(entry)}
                      <span className="font-normal text-grey-500"> · {actionLabel(entry)}</span>
                      {entry.subjectDeleted && (
                        <span className="ml-2 rounded-xs border border-grey-200 bg-grey-50 px-1.5 py-0.5 text-xs font-normal text-grey-700">
                          {entry.kind === "script" ? "No longer at this scope" : "No longer in the library"}
                        </span>
                      )}
                    </p>
                    <p className="text-grey-600">
                      {memberName(entry.memberId)} · {new Date(entry.at).toLocaleString()} · revision {entry.revision}
                      {entry.kind === "script" && <> · {scopeLabel(entry)}</>}
                    </p>
                  </div>
                  <span className={filterClasses("mt-0.5 shrink-0 text-grey-500")} aria-label={expanded ? "Hide details" : "Show details"}>
                    {expanded ? <IoChevronUp size={16} /> : <IoChevronDown size={16} />}
                  </span>
                </button>
                {expanded && (
                  <div className="border-t border-grey-100 px-3 py-3">
                    {entry.kind === "script" ? <ScriptSnapshot entry={entry} library={library} /> : <StepSnapshot entry={entry} />}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {nextBefore && (
        <Button variant={ButtonVariant.GHOST} size={ButtonSize.SMALL} disabled={loadingMore} onClickAsync={handleLoadMore}>
          {loadingMore ? "Loading…" : "Show earlier changes"}
        </Button>
      )}
    </section>
  );
}
